'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  ControlIds, 
  type CalculatorConfig, 
  type ControlValues,
  type MultiColumnSelectControl,
  type TextControl,
  type NumberControl,
  type SelectControl
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { LazyTabLabECharts } from '@repo/ui';
import type { EChartsOption } from 'echarts';
import { calculateBoxPlotStats, type BoxPlotStats } from '../../../lib/stats';

// Type for box plot specific control values
export interface BoxPlotControlValues extends ControlValues {
  [ControlIds.Values]: string[];
  [ControlIds.Title]: string;
  [ControlIds.Orientation]: 'horizontal' | 'vertical';
}

// Define the controls for the box plot
export const boxPlotControls = {
  tabs: [
    {
      title: 'Data',
      controls: [
        {
          id: ControlIds.Values,
          type: ControlType.MultiColumnSelect,
          label: 'Data columns',
          required: true,
          numericOnly: true,
          minSelections: 1
        } satisfies MultiColumnSelectControl,
        {
          id: 'title' as const,
          type: ControlType.Text,
          label: 'Title',
          required: false
        } satisfies TextControl
      ]
    },
    {
      title: 'Appearance',
      controls: [
        {
          id: ControlIds.Orientation,
          type: ControlType.Select,
          label: 'Orientation',
          options: [
            { label: 'Vertical', value: 'vertical' },
            { label: 'Horizontal', value: 'horizontal' }
          ],
          defaultValue: 'vertical'
        } satisfies SelectControl
      ]
    }
  ]
};

// Pure function to generate ECharts config for a box plot
export function getBoxPlotEChartsConfig({
  columns,
  stats,
  title,
  orientation
}: {
  columns: { id: string; name: string }[];
  stats: BoxPlotStats[];
  title?: string;
  orientation: 'horizontal' | 'vertical';
}): EChartsOption {
  const isHorizontal = orientation === 'horizontal';
  const categoryData = columns.map(col => col.name);
  
  return {
    title: {
      text: title || 'Score Distributions',
      left: 'center',
      top: 2,
      padding: [0, 0, 0, 0]
    },
    tooltip: {
      trigger: 'item',
      axisPointer: {
        type: 'shadow'
      },
      formatter: (params: any) => {
        const data = params.data;
        const columnName = params.name;
        let tooltip = `<div style="font-size: 14px;">
          <span style="font-weight: bold; color: ${params.color}">${columnName}</span><br/>
          <div style="margin: 10px 0;">
            <div style="margin: 5px 0;"><b>Maximum:</b> ${data[4].toFixed(2)}</div>
            <div style="margin: 5px 0;"><b>Upper Quartile:</b> ${data[3].toFixed(2)}</div>
            <div style="margin: 5px 0;"><b>Median:</b> ${data[2].toFixed(2)}</div>
            <div style="margin: 5px 0;"><b>Lower Quartile:</b> ${data[1].toFixed(2)}</div>
            <div style="margin: 5px 0;"><b>Minimum:</b> ${data[0].toFixed(2)}</div>
          </div>`;
        
        // Add outliers if present
        if (data.length > 5) {
          const outliers = data.slice(5);
          tooltip += `<div style="margin-top: 5px;">
            <b>Outliers:</b> ${outliers.map((val: number) => val.toFixed(2)).join(', ')}
          </div>`;
        }
        
        tooltip += '</div>';
        return tooltip;
      }
    },
    toolbox: {
      show: true,
      feature: {
        saveAsImage: {
          title: 'Save as Image',
          name: 'box-plot'
        },
        dataZoom: {
          title: {
            zoom: 'Zoom',
            back: 'Zoom Reset'
          }
        }
      }
    },
    grid: {
      left: '5%',
      right: '3%',
      bottom: '5%',
      top: '10%',
      containLabel: true
    },
    xAxis: isHorizontal ? {
      type: 'value',
      splitLine: { show: true },
      splitArea: { show: true },
      scale: true
    } : {
      type: 'category',
      data: categoryData,
      boundaryGap: true,
      splitLine: { show: false },
      splitArea: { show: false }
    },
    yAxis: isHorizontal ? {
      type: 'category',
      data: categoryData,
      boundaryGap: true,
      splitLine: { show: false },
      splitArea: { show: false }
    } : {
      type: 'value',
      splitLine: { show: true },
      splitArea: { show: true },
      scale: true
    },
    series: [
      {
        name: 'Box Plot',
        type: 'boxplot' as const,
        data: stats.map(stat => [
          stat.min,
          stat.q1,
          stat.median,
          stat.q3,
          stat.max
        ]),
        boxWidth: [7, 15],
        itemStyle: {
          borderWidth: 2
        },
        emphasis: {
          disabled: true
        }
      }
    ]
  };
}

export const boxPlotConfig: CalculatorConfig<CalculatorId.BoxPlot> = {
  id: CalculatorId.BoxPlot,
  type: CalculatorType.Graph,
  name: 'Box Plot',
  initialData: TableDataJSON.from({
    columns: [
      { 
        id: 'math_scores', 
        name: 'Math Scores', 
        values: ['72', '75', '78', '80', '81', '82', '83', '85', '86', '87', '88', '89', '91', '92', '95', '99'] 
      },
      {
        id: 'science_scores',
        name: 'Science Scores',
        values: ['65', '68', '70', '72', '75', '78', '80', '82', '85', '88', '90', '92', '95', '96', '98', '100']
      },
      {
        id: 'english_scores',
        name: 'English Scores',
        values: ['70', '73', '75', '77', '79', '80', '82', '84', '86', '87', '89', '91', '93', '94', '96', '97']
      }
    ]
  }),
  initialControlValues: {
    [ControlIds.Values]: ['math_scores', 'science_scores', 'english_scores'],
    [ControlIds.Title]: 'Subject Score Distributions',
    [ControlIds.Orientation]: 'vertical'
  },
  controls: boxPlotControls,
  getInitialConfig: (data: TableDataJSON) => {
    const numericColumns = data.columns.filter(col => 
      col.values.every(val => !isNaN(Number(val)))
    );

    return {
      [ControlIds.Values]: numericColumns.map(col => col.id),
      [ControlIds.Title]: 'Score Distributions',
      [ControlIds.Orientation]: 'vertical'
    };
  },
  getVisualizationData: ({ data, controls }) => {
    const boxPlotControls = controls as BoxPlotControlValues;
    
    if (!data?.columns?.length || !boxPlotControls[ControlIds.Values]?.length) {
      return null;
    }

    const selectedColumns = boxPlotControls[ControlIds.Values]
      .map(id => data.columns.find(col => col.id === id))
      .filter((col): col is typeof data.columns[0] => col !== undefined);

    if (!selectedColumns.length) {
      return null;
    }

    const stats = selectedColumns.map(column => {
      const numericData = column.values
        .map(val => Number(val))
        .filter(val => !isNaN(val));
      return calculateBoxPlotStats(numericData);
    });

    return getBoxPlotEChartsConfig({
      columns: selectedColumns,
      stats,
      title: boxPlotControls[ControlIds.Title],
      orientation: boxPlotControls[ControlIds.Orientation]
    });
  },
  render: ({ visualizationData }) => 
    visualizationData ? (
      <LazyTabLabECharts option={visualizationData} className="h-full w-full" />
    ) : null
}; 